/*
console.log("🚀 ApiClient v3 loaded:", new Date().toISOString());
 * @file: index.js
 * @description: It Contain rest functions for api call .
 * @author: Poonam
 */
import axios from 'axios';
import querystring from 'querystring';
import { setAuthorizationToken } from '../auth';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import loader from '../loader';
import environment from '../../environment';
import methodModel from '../methods';

import { logout } from '../../Pages/actions/user';

//console.log('🚀 ApiClient loaded at:', new Date().toISOString(), 'v2.0');

// cw: track in-flight requests globally so the service worker can defer post-deploy
// reloads until nothing is loading (see serviceWorkerRegistration.ts).
if (typeof window !== 'undefined') {
    window.__activeRequests = 0;
    const dec = () => { window.__activeRequests = Math.max(0, (window.__activeRequests || 1) - 1); };
    axios.interceptors.request.use(
        (config) => { window.__activeRequests = (window.__activeRequests || 0) + 1; return config; },
        (error) => { dec(); return Promise.reject(error); }
    );
    axios.interceptors.response.use(
        (response) => { dec(); return response; },
        (error) => { dec(); return Promise.reject(error); }
    );
}

// Helper function to get config with Authorization header
const getConfig = () => {
    const token = localStorage.getItem('token');
    return {
        headers: { 
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        },
    };
};

var baseUrl = environment.api

// cw: display error after 12 hr login expiration
// Track if we're already handling a 401 to prevent multiple toasts/redirects
let isHandling401 = false;

const handleError = (err, hideError, status) => {
    // Check for 401 status OR expired token in response
    if (status === 401 || err?.expired === true || err?.code === 401) {
        const path = window.location.pathname;
        
        // Skip if already on login page, OTP, RSVP confirmation, or already handling 401
        if (path.includes('/login') || path.includes('/otp') || path.includes('/rsvp-confirmation') || path.includes('/event-checkin') || isHandling401) {
            return;
        }
        
        // Prevent multiple 401 handlers from running
        isHandling401 = true;
        
        // Show appropriate message
        const message = err?.expired || err?.message?.includes('expired') 
            ? 'Session expired. Please sign in again.'
            : 'Please sign in to continue.';
        toast.error(message);
        // Preserve trusted device token across logouts so "Skip OTP for 30 days" survives
        const trustedToken = localStorage.getItem('trustedDeviceToken');
        const rememberMe = localStorage.getItem('remember');
        localStorage.clear();
        if (trustedToken) localStorage.setItem('trustedDeviceToken', trustedToken);
        if (rememberMe) localStorage.setItem('remember', rememberMe);
        
        // Small delay to ensure toast shows before redirect
        setTimeout(() => {
            window.location.href = '/login';
            // Reset flag after redirect
            setTimeout(() => { isHandling401 = false; }, 1000);
        }, 100);
        
        return;
    }
    
    // Show error message for non-401 errors
    if (!hideError && err?.message) {
        toast.error(err.message);
    }
}



class ApiClient {
    static post(url1, params, base = '', hideError = false) {
        let url = baseUrl + url1
        if (base) url = base + url1
    
        // Set the token in axios defaults before making request
        const token = localStorage.getItem('token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    
        return new Promise(function (fulfill, reject) {
            axios
                .post(url, params, {
                    headers: { 
                        'Content-Type': 'application/json'
                    }
                })
                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                        handleError(eres.data, hideError, eres?.status)
                        fulfill({ ...eres.data, success: false });
                    } else {
                        toast.error('Network Error')
                        reject(error);
                    }
                });
        });
    }

    static put(url1, params, base = '') {
        let url = baseUrl + url1
        if (base) url = base + url1
        
        // Set the token in axios defaults before making request
        const token = localStorage.getItem('token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
        
        return new Promise(function (fulfill, reject) {
            axios
                .put(url, params, {
                    headers: { 
                        'Content-Type': 'application/json'
                    }
                })
                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                        handleError(eres.data, false, eres?.status)
                        fulfill(eres.data);
                    } else {
                        toast.error('Network Error')
                        reject(error);
                    }
                });
        });
    }

    static get(url1, params = {}, base = '', hidError = '') {
        let url = baseUrl + url1
        if (base) url = base + url1
    
        let query = querystring.stringify(params);
        url = query ? `${url}?${query}` : url;
        
        // Set the token in axios defaults before making request
        const token = localStorage.getItem('token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
        
        return new Promise(function (fulfill, reject) {
            axios
                .get(url)
                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                        handleError(eres.data, hidError , eres?.status)
                        fulfill({ ...eres.data, success: false });
                    } else {
                        toast.error('Network Error')
                        reject(error);
                    }
                });
        });
    }

    static delete(url1, params = {}, base = '') {
        let url = baseUrl + url1
        if (base) url = base + url1
    
        let query = querystring.stringify(params);
        url = query ? `${url}?${query}` : url;
        
        // Set the token in axios defaults before making request
        const token = localStorage.getItem('token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
        
        return new Promise(function (fulfill, reject) {
            axios
                .delete(url)
                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                        handleError(eres.data, false, eres?.status)
                        fulfill(eres.data);
                    } else {
                        toast.error('Network Error')
                        reject(error);
                    }
                });
        });
    }

    static allApi(url, params, method = 'get') {
        if (method == 'get') {
            return this.get(url, params)
        } else if (method == 'put') {
            return this.put(url, params)
        } if (method == 'post') {
            return this.post(url, params)
        }
    }

    /*************** Form-Data Method ***********/
    static postFormData(url, params) {
        url = baseUrl + url
        return new Promise(function (fulfill, reject) {
            var body = new FormData();
            let oArr = Object.keys(params)
            oArr.map(itm => {
                body.append(itm, params[itm]);
            })

            const token = localStorage.getItem('token');
            const config = {
                headers: { 
                    //'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
            };

            axios
                .post(url, body, config)

                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                       
                        handleError(eres.data, false, eres?.status)
                        fulfill(eres.data);
                    } else {
                        toast.error('Network Error')
                        reject(error);
                    }
                });
        });
    }

    static postFormFileData(url, params) {
        url = baseUrl + url
        return new Promise(function (fulfill, reject) {
            var body = new FormData();
            let oArr = Object.keys(params)
            oArr.map(itm => {
                body.append(itm, params[itm]);
            })

            const token = localStorage.getItem('token');
            const configupdate = {
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
            };

            axios
                .post(url, body, configupdate)

                .then(function (response) {
                    fulfill(response && response.data);
                })
                .catch(function (error) {
                    loader(false)
                    if (error && error.response) {
                        let eres = error.response;
                     
                        handleError(eres.data, false, eres?.status)
                        fulfill(eres.data);
                    } else {
                        toast.error('Network Error');
                        reject(error);
                    }
                });
        });
    }


    static async multiImageUpload(url, params) {
        url = baseUrl + url
        var body = new FormData();
        let oArr = Object.keys(params)
        oArr.map(itm => {
            body.append(itm, params[itm]);
        })
        
        // Set the token in axios defaults before making request
        const token = localStorage.getItem('token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    
        return await axios
            .post(url, body)
            .then(function (response) {
                return response && response.data
            })
            .catch(function (error) {
                return error && error.response
            });
    }
    
    static async azureUpload({ file }) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${baseUrl}api/upload/blob`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });
            if (res.data?.success) {
                return { success: true, fileName: res.data.fileName };
            }
            return { success: false, message: res.data?.message || 'Upload failed' };
        } catch (err) {
            return { success: false, message: err.message || 'Upload failed' };
        }
    }

    static async importPhotoList({ file }) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${baseUrl}api/event/import-photo-list`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });
            return res.data;
        } catch (err) {
            return { success: false, message: err.message || 'Import failed' };
        }
    }

    static async azureBlobDelete({ fileName }) {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.delete(`${baseUrl}api/upload/blob-delete`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                data: { fileName }
            });
            return { success: res.data?.success || false };
        } catch (err) {
            return { success: false, message: err.message || 'Delete failed' };
        }
    }

}

export default ApiClient;